import { useMemo } from 'react';

import clsx from 'clsx';
import { entries, get } from 'lodash';

import { useFormatTimeIndicator } from '@/hooks/format/useFormatTimeIndicator';
import useFormatGraphData from '@/utils/hooks/charts/useFormatGraphData';
import useTranslation from '@/utils/hooks/translation/useTranslation';

import faqUrl from '@/utils/urls/faqUrl';
import useDarkMode from '@/utils/hooks/utils/useDarkMode';

import PropTypes from 'prop-types';

import { ignoredPattern } from '@/lib/analytics/charts/legendIgnoredPattern';
import { darkModeColorsChart } from '@/lib/analytics/colors';
import { url_faq_estimated_followers } from '@/lib/urls/faqUrls';

import Icon from '@/components/base/Icon';
import LinkBase from '@/components/base/LinkBase';
import Tooltip from '@/components/base/Tooltip';
import { Trans } from '@/components/tools/translation';

const legendBase = 'inline-block mr-1.5 align-middle';

const typeLegend = {
    line: 'w-3 h-0.5 rounded-full',
    square: 'w-2 h-2 rounded-sm',
    heatMap:
        'h-2 w-6 bg-gradient-to-r from-icoRed to-icoYellow-200 dark:from-icoDarkMode-red dark:to-icoDarkMode-yellow sm:mr-5',
    bttp: 'h-2 w-6 bg-gradient-to-r from-icoBlue to-icoBlue-200 dark:from-icoBlue dark:to-icoBlue/15 sm:mr-5'
};

const getLegend = (data, type, payload, theme) => {
    switch (type) {
        case 'reaction':
            return data?.graphData?.map(({ reaction }) =>
                payload.push({
                    component: (
                        <Icon
                            icon={`${reaction}-reaction`}
                            className="text-icoGray-500 dark:text-icoDarkMode-wolf"
                        />
                    ),
                    color: data.color ? darkModeColorsChart[theme][data.color[reaction]] : null,
                    reaction
                })
            );
        case 'reactions':
            return ['like', 'love', 'haha', 'wow', 'sad', 'angry'].map((reaction) =>
                payload.push({
                    component: (
                        <Icon
                            icon={`${reaction}-reaction`}
                            className="text-icoGray-500 dark:text-icoDarkMode-wolf"
                        />
                    ),
                    color: data.color ? darkModeColorsChart[theme][data.color[reaction]] : null,
                    reaction
                })
            );
    }
};

const LEGEND_CUSTOM_VARIANTS = {
    default: 'mt-3 flex flex-wrap justify-end gap-x-0 sm:gap-x-5 items-center',
    percentages: 'mt-1 flex gap-x-0 sm:gap-x-5 flex-wrap items-center'
};

const EstimatedLegend = ({ data, forceLightMode }) => {
    const { t } = useTranslation('analytics');
    const { isDarkMode } = useDarkMode();
    const theme = isDarkMode() && !forceLightMode ? 'dark' : 'light';

    return (
        <>
            <li className="flex items-center py-1 text-center">
                <span
                    className={clsx(legendBase, typeLegend[data.legendTypes?.unavailable])}
                    style={{
                        backgroundColor: darkModeColorsChart[theme][data.color?.unavailable]
                    }}
                />
                {t('unavailable_data')}
            </li>
            <li className="flex items-center py-1 text-center">
                <div className="mr-1.5 flex h-0.5 w-3 items-center gap-x-0.5">
                    {Array.apply(null, { length: 3 }).map((e, index) => (
                        <span
                            key={index}
                            className="h-0.5 w-1/3 rounded-full"
                            style={{
                                backgroundColor: darkModeColorsChart[theme][data.color?.estimated]
                            }}
                        />
                    ))}
                </div>
                <Trans
                    i18nKey="analytics:estimated_data_legend"
                    components={{
                        tag0: (
                            <LinkBase
                                key="learn_more"
                                target="_blank"
                                className="underline"
                                href={faqUrl(url_faq_estimated_followers)}
                                rel="help noopener noreferrer"
                            />
                        )
                    }}
                />
            </li>
        </>
    );
};

const ButtonLegend = ({
    hiddenKeys,
    entry,
    handleToggleKeysLegend,
    displayTooltip = true,
    children
}) => {
    const { t } = useTranslation('analytics');
    const objectHiddenKeys = hiddenKeys ? Object.keys(hiddenKeys) : [];

    if (
        displayTooltip &&
        objectHiddenKeys?.length > 1 &&
        !(
            objectHiddenKeys?.length === 2 &&
            objectHiddenKeys.includes('avg') &&
            entry?.key !== 'avg'
        ) &&
        !(
            objectHiddenKeys?.length === 3 &&
            objectHiddenKeys.includes('estimated') &&
            objectHiddenKeys.includes('no_data')
        )
    ) {
        return (
            <Tooltip
                position="top"
                content={t(
                    hiddenKeys[entry?.key || entry?.reaction] ? 'commons:show' : 'commons:hide'
                )}>
                <button
                    onClick={() => handleToggleKeysLegend(entry?.key || entry?.reaction)}
                    data-testid="legend-button">
                    {children}
                </button>
            </Tooltip>
        );
    } else {
        return children;
    }
};

ButtonLegend.propTypes = {
    hiddenKeys: PropTypes.object,
    entry: PropTypes.object,
    handleToggleKeysLegend: PropTypes.func,
    children: PropTypes.node
};

const HighLightLegend = ({ className, data, theme, highlightValue, activeFilter }) => {
    const { formatHour } = useFormatTimeIndicator();
    const { t } = useTranslation('analytics');

    const isBestTimeToPost = useMemo(
        () =>
            ['best_time_to_post', 'stories_best_time_to_post', 'posting_habits'].includes(data.id),
        [data.id]
    );

    const getTranslationValues = () => {
        let values = {};

        if (highlightValue) {
            data.legendHighlightValueTranslation.values.forEach((entry) => {
                values[entry] =
                    entry === 'day'
                        ? t(`commons:days.${highlightValue[entry]}`)
                        : entry === 'date'
                          ? formatHour(highlightValue[entry])
                          : highlightValue[entry];
            });
        }

        return values;
    };

    return (
        <ul
            className={clsx(
                'mt-3 flex items-center space-x-3 rounded-full py-1 pl-1 pr-5 text-xs',
                className
            )}>
            <Icon
                icon={data.iconHighestValue}
                data-testid="heat-map-legend-icon"
                className="h-6 w-6 rounded-full p-1.5 text-xs text-white"
                style={{
                    backgroundColor:
                        data.colors[theme][data.breakpoints[data.breakpoints.length - 1].color],
                    color:
                        isBestTimeToPost && theme === 'light'
                            ? 'white'
                            : darkModeColorsChart[theme][data.color?.value]
                }}
            />
            <p>
                <Trans
                    i18nKey={
                        data.legendHighlightValueTranslation.filtersToken
                            ? `analytics:${data.legendHighlightValueTranslation.filtersToken[activeFilter]}`
                            : `analytics:${data.legendHighlightValueTranslation.token}`
                    }
                    values={getTranslationValues()}
                />
            </p>
        </ul>
    );
};

const LegendCustom = ({
    data,
    activeFilter = null,
    type = 'line',
    variant = 'default',
    handleToggleKeysLegend,
    hiddenKeys,
    forceLightMode = false,
    className
}) => {
    const { t } = useTranslation('analytics');
    const { isDarkMode } = useDarkMode();
    const { formatPercentValue } = useFormatGraphData();
    const theme = isDarkMode() && !forceLightMode ? 'dark' : 'light';
    const showPercentages = LEGEND_CUSTOM_VARIANTS[variant] === LEGEND_CUSTOM_VARIANTS.percentages;

    const isHeatMap = useMemo(
        () =>
            ['best_time_to_post', 'fans_online', 'followers_online', 'posting_habits'].includes(
                data.id
            ),
        [data.id]
    );

    const isEstimatedChart = useMemo(
        () =>
            Boolean(
                data?.graphData?.find(
                    (entry) => 'estimated' in entry && typeof entry.estimated === 'number'
                )
            ),
        [data?.graphData]
    );

    const isNumberLegend = data.id === 'posts_distribution';
    const highlightValue = useMemo(
        () => data?.graphData?.find((item) => item.highlight),
        [data?.graphData]
    );

    const legendData = useMemo(() => {
        const payload = [];

        if (data?.customTick) {
            getLegend(data, data?.customTick?.type, payload, theme);
        } else {
            for (const [key, value] of entries(get(data, 'graphData[0]', {}))) {
                if (data.id === 'best_time_to_post' && key !== 'value' && key !== 'day') {
                    continue;
                }
                if (
                    ((data.dataValuesProperties
                        ? data.dataValuesProperties.includes(key)
                        : !ignoredPattern.includes(key)) &&
                        data.name) ||
                    ((data.id === 'fans_online' ||
                        data.id === 'followers_online' ||
                        data.id === 'best_time_to_post') &&
                        key === 'day')
                ) {
                    let nameLegend = data.name[key];

                    if (typeof nameLegend === 'object' && activeFilter) {
                        nameLegend = nameLegend[activeFilter] ?? '';
                    }

                    let legendValue = '';
                    if (isNumberLegend) {
                        legendValue = nameLegend;
                    } else {
                        if (nameLegend?.raw) {
                            legendValue = nameLegend?.raw;
                        } else {
                            legendValue = typeof nameLegend === 'string' ? t(nameLegend) : '';
                        }
                    }

                    payload.push({
                        color: data.color ? darkModeColorsChart[theme][data.color[key]] : null,
                        value: legendValue,
                        key,
                        percent: value !== null ? formatPercentValue(value, data?.graphData) : 'N/A'
                    });
                }
            }
        }

        if (data.avg) {
            let nameLegendAvg = data.name['avg'];
            if (typeof nameLegendAvg === 'object' && activeFilter) {
                nameLegendAvg = nameLegendAvg[activeFilter] ?? '';
            }
            payload.push({
                color: darkModeColorsChart[theme].referenceLine,
                value: nameLegendAvg?.raw || t(nameLegendAvg)
            });
        }

        return payload;
    }, [data, activeFilter]);

    return (
        <>
            {data.name ? (
                <div
                    className={clsx(
                        'flex flex-col items-center space-x-2 sm:flex-row',
                        isHeatMap ? 'justify-between' : 'justify-end'
                    )}>
                    {highlightValue && (
                        <HighLightLegend
                            className={className}
                            data={data}
                            theme={theme}
                            highlightValue={highlightValue}
                            activeFilter={activeFilter}
                        />
                    )}
                    <ul
                        className={clsx(
                            'min-h-[2rem] rounded-full px-5 py-1 text-xs',
                            LEGEND_CUSTOM_VARIANTS[variant],
                            className
                        )}>
                        {isEstimatedChart ? (
                            <EstimatedLegend data={data} forceLightMode={forceLightMode} />
                        ) : null}
                        {legendData.map((entry, index) => {
                            if (entry.key !== 'estimated' && entry.key !== 'no_data') {
                                return (
                                    <ButtonLegend
                                        key={entry.value + entry.color}
                                        handleToggleKeysLegend={handleToggleKeysLegend}
                                        entry={entry}
                                        displayTooltip={!isHeatMap}
                                        hiddenKeys={hiddenKeys}>
                                        <li
                                            className={clsx('flex items-center py-1 text-center', {
                                                'text-icoGray-400':
                                                    hiddenKeys?.[entry?.key || entry?.reaction]
                                            })}>
                                            {hiddenKeys?.[entry?.key || entry?.reaction] ? (
                                                <Icon icon="invisible" className={legendBase} />
                                            ) : (
                                                (!isHeatMap ||
                                                    (isHeatMap &&
                                                        index === legendData.length - 1)) && (
                                                    <span
                                                        className={clsx(
                                                            legendBase,
                                                            data.legendTypes
                                                                ? typeLegend[
                                                                      data.legendTypes[
                                                                          entry.key ||
                                                                              entry.reaction
                                                                      ]
                                                                  ]
                                                                : typeLegend[type] ?? ''
                                                        )}
                                                        style={{
                                                            backgroundColor: isHeatMap
                                                                ? 'transparent'
                                                                : entry.color
                                                        }}
                                                    />
                                                )
                                            )}
                                            {entry.value || entry?.component}
                                            {showPercentages && (
                                                <span className="ml-1">- {entry.percent}</span>
                                            )}
                                        </li>
                                    </ButtonLegend>
                                );
                            }
                        })}
                    </ul>
                </div>
            ) : (
                highlightValue && (
                    <div
                        className={clsx(
                            'flex items-center',
                            isHeatMap ? 'justify-between' : 'justify-end'
                        )}>
                        <HighLightLegend
                            className={className}
                            data={data}
                            theme={theme}
                            highlightValue={highlightValue}
                            activeFilter={activeFilter}
                        />
                    </div>
                )
            )}
        </>
    );
};

LegendCustom.propTypes = {
    data: PropTypes.object,
    activeFilter: PropTypes.string,
    type: PropTypes.string,
    variant: PropTypes.string,
    handleToggleKeysLegend: PropTypes.func,
    hiddenKeys: PropTypes.object,
    className: PropTypes.string,
    forceLightMode: PropTypes.bool
};

export default LegendCustom;
